import React, { useState, useEffect, useRef } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import 'keen-slider/keen-slider.min.css';

const whyChooseData = [
    {
        id: 1,
        title: "Experienced Team",
        desc: "A skilled team of developers, designers, and technology experts dedicated to delivering excellence."
    },
    {
        id: 2,
        title: "Innovative Solutions",
        desc: "Future-ready technologies and creative problem-solving tailored to your business needs."
    },
    {
        id: 3,
        title: "Client-Centric Approach",
        desc: "We align our digital strategies directly with your long-term roadmap and metrics."
    },
    {
        id: 4,
        title: "Quality Assurance",
        desc: "Rigorous testing and international compliance standards to deliver robust digital assets."
    }
];

const WhyChooseMobikode = () => {
    const [activeId, setActiveId] = useState(1);
    const [loaded, setLoaded] = useState(false);
    const timerRef = useRef(null);

    const [sliderRef, instanceRef] = useKeenSlider({
        loop: true,
        initial: 0,
        slides: {
            perView: 1.1,
            spacing: 16,
        },
        breakpoints: {
            '(min-width: 640px)': {
                slides: { perView: 2.2, spacing: 20 },
            },
            '(min-width: 1024px)': {
                slides: { perView: 3.2, spacing: 24 },
            },
            '(min-width: 1440px)': {
                slides: { perView: 3.8, spacing: 24 },
            },
        },
        slideChanged(slider) {
            const relIdx = slider.track.details.rel;
            if (whyChooseData[relIdx]) {
                setActiveId(whyChooseData[relIdx].id);
            }
        },
        created(s) {
            setLoaded(true);
            startAutoplay(s);
            s.container.addEventListener("mouseover", () => clearTimeout(timerRef.current));
            s.container.addEventListener("mouseout", () => startAutoplay(s));
        },
        destroyed() {
            clearTimeout(timerRef.current);
        }
    });

    const startAutoplay = (sliderInstance) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (sliderInstance && !sliderInstance.destroyed) {
                sliderInstance.next();
                startAutoplay(sliderInstance);
            }
        }, 4000);
    };

    return (
        <section className="w-full bg-[#FFFFFF] py-16 sm:py-20 lg:py-24 overflow-hidden select-none text-left relative z-10">
            <div className="max-w-[1440px] w-full mx-auto px-5 sm:px-10 lg:px-[80px] lg:pr-0">

                <div className="w-full flex items-center justify-between gap-6 mb-16 lg:pr-[80px]">
                    <h2 className="text-[#01386A] font-sora font-[600] text-[20px] lg:text-[30px] leading-[1.2] tracking-tight">
                        Why Choose Mobikode
                    </h2>

                    {loaded && instanceRef.current && (
                        <div className="flex items-center gap-3 shrink-0">
                            <button
                                onClick={() => instanceRef.current?.prev()}
                                className="w-10 h-10 border border-[#ED7A3A] bg-transparent flex items-center justify-center text-[#ED7A3A] hover:bg-[#ED7A3A] hover:text-white transition-all duration-300 cursor-pointer rounded-full"
                                aria-label="Previous slide"
                            >
                                <ArrowLeft size={18} strokeWidth={2} />
                            </button>
                            <button
                                onClick={() => instanceRef.current?.next()}
                                className="w-10 h-10 border border-[#ED7A3A] bg-transparent flex items-center justify-center text-[#ED7A3A] hover:bg-[#ED7A3A] hover:text-white transition-all duration-300 cursor-pointer rounded-full"
                                aria-label="Next slide"
                            >
                                <ArrowRight size={18} strokeWidth={2} />
                            </button>
                        </div>
                    )}
                </div>

                <div className="w-full flex items-end min-h-[340px]">
                    <div ref={sliderRef} className="keen-slider">
                        {whyChooseData.map((slide) => {
                            const isSelected = slide.id === activeId;

                            return (
                                <div
                                    key={slide.id}
                                    className="keen-slider__slide "
                                    onMouseEnter={() => setActiveId(slide.id)}
                                >
                                    <div className="h-full flex flex-col justify-end">
                                        <div
                                            className={`w-full p-6 sm:p-8 flex flex-col justify-between items-start rounded-none border transition-all duration-[600ms] ease-[cubic-bezier(0.25,1,0.5,1)] relative overflow-hidden group ${isSelected
                                                ? "h-[280px] sm:h-[320px] bg-[#01386A] border-transparent shadow-[0_15px_35px_rgba(10,61,98,0.15)]"
                                                : "h-[116px] bg-white border-[#E2E8F0]"
                                                }`}
                                        >
                                            <div className="w-full">
                                                <h3 className={`font-sora text-[16px] sm:text-[20px] font-[600] tracking-tight transition-colors duration-300 ${isSelected ? "text-white" : "text-[#0A2540]"
                                                    }`}>
                                                    {slide.title}
                                                </h3>
                                            </div>

                                            <div className={`w-full transition-all duration-500 ease-in-out ${isSelected
                                                ? "opacity-100 transform translate-y-0"
                                                : "opacity-0 transform translate-y-4 pointer-events-none"
                                                }`}>
                                                <p className="text-white font-graphik text-[14px] sm:text-[16px] leading-relaxed font-[500]">
                                                    {slide.desc}
                                                </p>
                                            </div>

                                            <div
                                                className={`absolute bottom-0 left-0 h-[4px] bg-[#01386A] transition-all duration-[700ms] ease-out origin-left w-full`}
                                            />
                                            <div
                                                className={`absolute bottom-0 left-0 h-[4px] bg-[#ED7A3A] transition-all duration-[700ms] ease-out origin-left ${isSelected ? "w-full" : "w-0"
                                                    }`}
                                            />

                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>
        </section>
    );
};

export default WhyChooseMobikode;